import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { UniversalNavLinksService } from '@app/core/services/universal-nav-links/universal-nav-links.service';
import { SkinnyBsshService } from '@app/core/services/skinny-bssh/skinny-bssh.service';
import { TrashItemsStore } from '@app/core/store/trash-items/trash-items.store';
import { CurrentUserStore } from '@app/user/store/current-user/current-user.store';
import { ShadowDomConfig } from '@app/core/directives/shadow-dom.directive';
import { IMenuLink } from '@bssh/comp-lib';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';
import { MICROARRAY_HUB_PATHS } from '@app/cloud-run-prep/constants';
import { RoutingStateService } from '@app/core/services/routing/routing-state.service';

export interface ISideNavItem extends IMenuLink {
  children?: IMenuLink[];
}

@Component({
  selector: 'app-bs-side-nav',
  templateUrl: './bs-side-nav.component.html',
  styleUrls: ['./bs-side-nav.component.scss']
})
export class BsSideNavComponent implements OnInit, OnDestroy, AfterViewInit {
  private subs = new SubSink();
  
  /** Reference to the rad-local-navigation element */
  @ViewChild('localNav', { static: false }) localNavRef: ElementRef;

  /** References to all rad-local-navigation-group elements */
  @ViewChildren('navGroup') navGroupRefs: QueryList<ElementRef>;
  
  /** Emits when side nav panel visibility changes */
  @Output() expandedChange = new EventEmitter<boolean>();
  
  public navItems: ISideNavItem[] = [];
  public currentUrl: string = '';
  public trashBadge: string = null;
  public trashBadgeConfig: ShadowDomConfig = null;
  public isExpanded: boolean = true;

  // Styles to inject into the Shadow DOM for the footer separator and trash badge
  public localNavStyles = `
    .rad-local-navigation__footer {
      border-top: 1px solid #e0e0e0;
      margin-top: 8px;
      padding-top: 8px;
    }
  `;

  // Correct the group label font-size (the component uses 1rem which resolves to
  // 10px under our :root { font-size: 62.5% } reset)
  public groupLabelStyles = `
    .rad-local-navigation-group button {
      font-size: 1.6rem;
    }
  `;

  // Styles for the trash badge (injected into trash navigation item's Shadow DOM)
  public trashBadgeStyles = `
    .trash-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      margin-left: 6px;
      border-radius: 8px;
      background-color: #6b7280;
      color: #ffffff;
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      vertical-align: middle;
      margin-bottom: 2px;
    }
  `;
  
  constructor(
    private router: Router,
    private routingStateService: RoutingStateService,
    private universalNavLinksService: UniversalNavLinksService,
    private skinnyBsshService: SkinnyBsshService,
    private trashItemsStore: TrashItemsStore,
    private currentUserStore: CurrentUserStore,
    private codeFeaturesService: CodeFeaturesService
  ) {}

  ngOnInit(): void {
    // Get navigation items (full version unless skinny bssh)
    const isFullVersion = !this.skinnyBsshService.isSkinnyBssh();
    this.navItems = this.buildNavItems(isFullVersion);
    
    // Set initial active route
    this.currentUrl = this.router.url;
    
    // Subscribe to route changes to update active state
    this.subs.sink = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.urlAfterRedirects;
      this.syncGroupExpansion();
      if (this.isErrorPage(event.urlAfterRedirects)) {
        this.expandNav();
      }
    });

    // Subscribe to code features to conditionally show Microarray scans
    this.subs.sink = this.currentUserStore.stateChanged.pipe(
      filter(state => state != null && state.currentUser != null && state.currentUserCodeFeatures != null)
    ).subscribe(() => {
      const isFullVersion = !this.skinnyBsshService.isSkinnyBssh();
      this.navItems = this.buildNavItems(isFullVersion);
      this.syncGroupExpansion();
    });

    // Subscribe to trash badge count
    this.subs.sink = this.trashItemsStore.stateChanged.pipe(
      filter((state) => !!state && state.trashItemsCount !== undefined)
    ).subscribe((state) => {
      this.trashBadge = this.formatTrashBadgeCount(state.trashItemsCount);
      this.updateTrashBadgeConfig();
    });

    // Subscribe to context changes (account/workgroup switch) to reset trash badge
    this.subs.sink = this.currentUserStore.contextChanged$.subscribe(() => {
      // Clear the badge immediately while new count is loading
      this.trashBadge = null;
      this.updateTrashBadgeConfig();
      // Refresh the trash count for the new context
      this.trashItemsStore.updateTrashItemsCount();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngAfterViewInit(): void {
    // Read initial expanded state from rad-local-navigation after it initializes
    // Use setTimeout to allow the component to fully initialize from session storage
    setTimeout(() => {
      if (this.localNavRef && this.localNavRef.nativeElement) {
        const initialExpanded = this.localNavRef.nativeElement.expanded;
        if (initialExpanded !== undefined) {
          this.isExpanded = initialExpanded;
          this.expandedChange.emit(initialExpanded);
        }
      }
      // Initial navigation has already fired before this subscription exists,
      // so check on first render whether we landed on an error page.
      if (this.isErrorPage(this.currentUrl)) {
        this.expandNav();
      }
    }, 0);

    this.syncGroupExpansion();
  }

  /**
   * Handle navigation item click
   */
  onItemClicked(event: any, item: IMenuLink): void {
    // The rad-local-navigation-item emits itemClicked when:
    // 1. User clicks (pointerEvent is the MouseEvent) - we should navigate
    // 2. Active is set programmatically (pointerEvent is null) - we should NOT navigate
    // Handle both direct event and CustomEvent (where payload is in event.detail)
    const eventData = (event && event.detail) ? event.detail : event;
    if (eventData && eventData.pointerEvent) {
      this.router.navigateByUrl(item.url);
    }
  }

  /**
   * Check if a group should be active/expanded based on whether any child is active
   */
  isGroupActive(item: ISideNavItem): boolean {
    if (!this.currentUrl || !item.children) {
      return false;
    }
    return item.children.some(child => this.isActive(child));
  }

  /**
   * Handle Settings click
   */
  onSettingsClicked(event: any): void {
    const eventData = (event && event.detail) ? event.detail : event;
    if (eventData && eventData.pointerEvent) {
      this.router.navigateByUrl('/settings');
    }
  }

  /**
   * Handle Resources click
   */
  onResourcesClicked(event: any): void {
    const eventData = (event && event.detail) ? event.detail : event;
    if (eventData && eventData.pointerEvent) {
      this.router.navigateByUrl('/resources');
    }
  }

  /**
   * Handle Trash click
   */
  onTrashClicked(event: any): void {
    const eventData = (event && event.detail) ? event.detail : event;
    if (eventData && eventData.pointerEvent) {
      this.router.navigateByUrl(this.universalNavLinksService.trashUrl);
    }
  }

  /**
   * Check if Settings is active
   */
  isSettingsActive(): boolean {
    return this.currentUrl.startsWith('/settings') && !this.isTrashActive();
  }

  /**
   * Check if Resources is active
   */
  isResourcesActive(): boolean {
    return this.currentUrl.startsWith('/resources');
  }

  /**
   * Check if Trash is active
   */
  isTrashActive(): boolean {
    return this.currentUrl.startsWith(this.universalNavLinksService.trashUrl);
  }

  /**
   * Format the trash badge count for display
   */
  private formatTrashBadgeCount(count: number): string | null {
    if (count === null || count === undefined || count === 0) {
      return null;
    } else if (count < 100) {
      return `${count}`;
    } else {
      return '99+';
    }
  }

  /**
   * Update the Shadow DOM config for the trash badge
   */
  private updateTrashBadgeConfig(): void {
    if (this.trashBadge) {
      this.trashBadgeConfig = {
        action: 'add',
        selector: 'a[part="item-link"]',
        html: `<span class="trash-badge">${this.trashBadge}</span>`,
        position: 'append'
      };
    } else {
      this.trashBadgeConfig = null;
    }
  }

  /**
   * Check if a nav item is active based on current URL
   */
  isActive(item: IMenuLink): boolean {
    if (!this.currentUrl || !item.url) {
      return false;
    }
    
    // Special case for Home/Dashboard - exact match only
    if (item.url === '/dashboard') {
      return this.currentUrl === '/dashboard' || this.currentUrl === '/';
    }

    // Match both /runs (list) and /run/:id (details). The trailing slash on
    // '/run/' prevents false-matching /run-planning which is a separate route.
    if (item.url === '/runs') {
      return this.currentUrl.startsWith('/runs') || this.currentUrl.startsWith('/run/');
    }

    // For other routes, check if URL starts with the nav item's URL
    return this.currentUrl.startsWith(item.url);
  }

  updateExpandedState(): void {
    setTimeout(() => {
      const expanded = this.localNavRef && this.localNavRef.nativeElement 
        ? this.localNavRef.nativeElement.expanded 
        : true;
      this.isExpanded = expanded;
      this.expandedChange.emit(expanded);
    }, 0);
  }

  /**
   * Imperatively sync group expansion: expand groups with an active child,
   * collapse groups with no active child.
   * Uses setTimeout to run after the web component's own session-storage-based
   * initialization, which would otherwise override attribute bindings.
   */
  private syncGroupExpansion(): void {
    setTimeout(() => {
      if (!this.navGroupRefs) { return; }
      const groupItems = this.navItems.filter(i => i.children);
      this.navGroupRefs.forEach((groupRef, index) => {
        const item = groupItems[index];
        if (item) {
          groupRef.nativeElement.expanded = this.isGroupActive(item);
        }
      });
    }, 0);
  }

  private isErrorPage(url: string): boolean {
    return this.routingStateService.isErrorPage(url);
  }

  private expandNav(): void {
    setTimeout(() => {
      if (this.localNavRef && this.localNavRef.nativeElement) {
        this.localNavRef.nativeElement.expanded = true;
        this.isExpanded = true;
        this.expandedChange.emit(true);
      }
    }, 0);
  }

  /**
   * Build nav items, converting the flat Runs item into a group with children.
   * Microarray scans child is included when the code feature is enabled OR when
   * the user is already on the microarray page (feature must be enabled if they got there).
   */
  private buildNavItems(isFullVersion: boolean): ISideNavItem[] {
    const baseItems = this.universalNavLinksService.getNavItems(isFullVersion);
    
    return baseItems.map(item => {
      if (item.title === 'Runs') {
        const children: IMenuLink[] = [
          { title: 'Sequencing runs', url: '/runs', disabled: false }
        ];
        if (this.codeFeaturesService.microarrayIsEnabled) {
          children.push({ title: 'Microarray scans', url: MICROARRAY_HUB_PATHS.ROOT_PATH, disabled: false });
        }
        return { ...item, children: children };
      }
      return item;
    });
  }
}
